<template>
  <div class="awards-container">
    <!-- Header Section -->
    <div class="awards-header" v-if="profileType == 'Company'">
      <div class="header-content">
        <h1 class="awards-title">
          <span class="title-icon">🏆</span>
          Awards
        </h1>
        <button 
          class="create-btn"
          @click="onCreateAward"
          :class="{ 'btn-loading': loading }"
        >
          <span class="btn-icon">+</span>
          Create Award
        </button>
      </div>
    </div>

    <!-- Awards Grid -->
    <div class="awards-grid">
      <transition-group 
        name="award-card" 
        tag="div" 
        class="grid-container"
      >
        <div 
          v-for="(award, index) in awards" 
          :key="award._id"
          class="award-wrapper"
          :style="{ 'animation-delay': `${index * 0.1}s` }"
        >
          <award-card
            :award="award"
            :eligibles="eligibles"
            class="award-card-component"
          />
        </div>
      </transition-group>

      <!-- Empty State -->
      <div v-if="awards.length == 0 && !loading" class="empty-state">
        <div class="empty-content">
          <div class="empty-icon">🎖️</div>
          <h3 class="empty-title">No Awards Available</h3>
          <p class="empty-description">
            {{ errorMessage && errorMessage.includes("No coupons found")
                ? "No awards to redeem at the moment."
                : "Awards will appear here once they're created." }}
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
        </div>
        <p class="loading-text">Loading awards...</p>
      </div>
    </div>

    <!-- Snackbar Notification -->
    <transition name="snackbar">
      <div v-if="snackbar" class="snackbar">
        <div class="snackbar-content">
          <span class="snackbar-icon">⚠️</span>
          <span class="snackbar-message">{{ errorMessage }}</span>
          <button class="snackbar-close" @click="snackbar = false">×</button>
        </div>
      </div>
    </transition>

    <!-- Create Awards Modal -->
    <transition name="modal">
      <div v-if="createDialog" class="modal-overlay" @click="onCloseDialog">
        <div class="modal-container" @click.stop>
          <create-awards
            :dialog="createDialog"
            :onClose="onCloseDialog"
            :eligibleList="eligibleList"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AwardCard from "./AwardCard.vue";
import CreateAwards from "./CreateAwards.vue";

export default {
  components: {
    CreateAwards,
    AwardCard
  },
  
  data() {
    return {
      loading: false,
      errorMessage: null,
      snackbar: false,
      loadCompleted: -1,
      loadingMore: false,
      picker: null,
      createDialog: false,
      eligibleList: [],
      eligibles: []
    };
  },

  methods: {
    ...mapActions("awards", {
      getAllAwards: "fetchAllAwards",
      getEligibles: "getEligibles"
    }),

    showErrorMessage(message) {
      console.log("Error:", message);
      console.log("eligible list:", this.eligibleList);
      console.log("eligibles:", this.awards);
      this.errorMessage = message;
      this.snackbar = true;
      
      // Auto-hide snackbar after 5 seconds
      setTimeout(() => {
        this.snackbar = false;
      }, 5000);
    },

    async loadData() {
      this.loading = true;
      
      try {
        await this.getAllAwards();
        
        if (this.profileType == "Company") {
          const eligibles = await this.getEligibles();
          this.eligibles = eligibles;
          this.eligibleList = eligibles;
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message || error;
        this.showErrorMessage(message);
      } finally {
        this.loading = false;
      }
    },

    onCreateAward() {
      this.createDialog = true;
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    },

    onCloseDialog() {
      this.createDialog = false;
      document.body.style.overflow = 'auto'; // Restore scroll
    },

    formatMinutes(value) {
      const hrs = Math.floor(value / 60);
      const mins = Math.floor(value % 60);
      return `${hrs}:${mins < 10 ? '0' : ''}${mins}`;
    },

    goToProfile(user) {
      this.$router.push({
        name: "volunteer-profile-details",
        params: { id: user._id }
      });
    }
  },

  computed: {
    ...mapGetters({
      awards: "awards/getAllAwards",
      profile: "profile",
      profileType: "auth/getType"
    })
  },

  mounted() {
    this.loadData();
  },

  beforeDestroy() {
    document.body.style.overflow = 'auto'; // Cleanup
  }
};
</script>

<style scoped>
/* CSS Variables for consistent theming */
:root {
  --primary-color: #6366f1;
  --primary-dark: #4f46e5;
  --secondary-color: #8b5cf6;
  --accent-color: #06b6d4;
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --error-color: #ef4444;
  --background: #f8fafc;
  --surface: #ffffff;
  --surface-hover: #f1f5f9;
  --text-primary: #1e293b;
  --text-secondary: #64748b;
  --text-muted: #94a3b8;
  --border-color: #e2e8f0;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  --radius-sm: 0.375rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
}

/* Dark mode variables */
@media (prefers-color-scheme: dark) {
  :root {
    --background: #0f172a;
    --surface: #1e293b;
    --surface-hover: #334155;
    --text-primary: #f1f5f9;
    --text-secondary: #cbd5e1;
    --text-muted: #64748b;
    --border-color: #334155;
  }
}

/* Main Container */
.awards-container {
  min-height: 100vh;
  background: var(--background);
  padding: 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
  line-height: 1.6;
}

/* Header Section */
.awards-header {
  margin-bottom: 2rem;
  animation: fadeInDown 0.6s ease-out;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.awards-title {
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  font-size: 2rem;
  filter: drop-shadow(0 2px 4px rgba(99, 102, 241, 0.3));
}

/* Create Button */
.create-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-lg);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
}

.create-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.create-btn:hover::before {
  left: 100%;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.create-btn:active {
  transform: translateY(0);
}

.btn-icon {
  font-size: 1.125rem;
  font-weight: 300;
}

.btn-loading {
  opacity: 0.7;
  cursor: not-allowed;
  pointer-events: none;
}

/* Awards Grid */
.awards-grid {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.grid-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.award-wrapper {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

.award-card-component {
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  background: var(--surface);
  border: 1px solid var(--border-color);
}

.award-card-component:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
  border-color: var(--primary-color);
}

/* Empty State */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  animation: fadeIn 0.6s ease-out;
}

.empty-content {
  text-align: center;
  max-width: 400px;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.empty-description {
  color: var(--text-secondary);
  font-size: 1rem;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  animation: fadeIn 0.6s ease-out;
}

.loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin-bottom: 1rem;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  border-top-color: var(--secondary-color);
  animation-delay: -0.1s;
}

.spinner-ring:nth-child(3) {
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  border-top-color: var(--accent-color);
  animation-delay: -0.2s;
}

.loading-text {
  color: var(--text-secondary);
  font-weight: 500;
}

/* Snackbar */
.snackbar {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: slideInUp 0.3s ease-out;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--surface);
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  border: 1px solid var(--border-color);
  min-width: 300px;
  max-width: 500px;
}

.snackbar-icon {
  font-size: 1.25rem;
}

.snackbar-message {
  flex: 1;
  font-weight: 500;
}

.snackbar-close {
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.snackbar-close:hover {
  background: var(--surface-hover);
  color: var(--text-primary);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--border-color);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translate(-50%, 100%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Transition Classes */
.award-card-enter-active,
.award-card-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.award-card-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.9);
}

.award-card-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.9);
}

.snackbar-enter-active,
.snackbar-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.snackbar-enter-from,
.snackbar-leave-to {
  opacity: 0;
  transform: translate(-50%, 100%);
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9) translateY(20px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .awards-container {
    padding: 0.5rem;
  }
  
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .awards-title {
    font-size: 2rem;
  }
  
  .grid-container {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }
  
  .create-btn {
    width: 100%;
    justify-content: center;
  }
  
  .snackbar-content {
    margin: 0 1rem;
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .grid-container {
    grid-template-columns: 1fr;
  }
  
  .awards-title {
    font-size: 1.75rem;
  }
  
  .modal-overlay {
    padding: 0.5rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus styles for accessibility */
.create-btn:focus,
.snackbar-close:focus {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  :root {
    --border-color: #000000;
    --shadow-md: 0 0 0 1px #000000;
  }
}
</style>

